import React, { useEffect, useState } from 'react';
import { Shield, CheckCircle, MapPin, Lock, Database, Search, Award } from 'lucide-react';

const AddressVerification = () => {
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    const hiddenElements = document.querySelectorAll('.initially-hidden');
    hiddenElements.forEach((el) => observer.observe(el));

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
      clearInterval(interval);
    };
  }, []);

  const processSteps = [
    {
      title: "AI-Based Assessment",
      description: "AI platform assesses candidate's ID accuracy, classifying images as related or unrelated",
      icon: <Database className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // AI and technology theme
    },
    {
      title: "GPS Confirmation",
      description: "Candidate must be present at the designated location for process completion",
      icon: <MapPin className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/5673489/pexels-photo-5673489.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // GPS and map theme
    },
    {
      title: "On-Site Verification",
      description: "A selected team physically visits to confirm the candidate's location",
      icon: <Search className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Team collaboration for on-site visit
    },
    {
      title: "Final Documentation",
      description: "GPS coordinates and location imagery are recorded for verification",
      icon: <Award className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/669610/pexels-photo-669610.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Documentation and records theme
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 to-gray-900 text-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/60 to-red-900/90 z-10"></div>
        
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
            alt="Address Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-20 container mx-auto px-4 h-full flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <div className="bg-white/10 p-3 rounded-full animate-pulse">
                <Shield className="h-8 w-8 text-red-300" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fadeInDown">
              Address Verification
            </h1>
            <p className="text-xl text-red-200 mb-8 max-w-2xl animate-fadeInUp">
              Confirming the accuracy of addresses to prevent fraud and improve data quality in both digital and physical environments.
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeInUp">
              <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
                <Lock className="w-5 h-5 mr-2" />
                Learn More
              </button>
              {/* <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
                Watch Demo
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Understanding Address Verification</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-lg leading-relaxed mb-6">
                Address verification is conducted to confirm the accuracy of a person's or business's address, ensuring it is valid and up-to-date. This process helps prevent fraud and improves data quality.
              </p>
              <p className="text-lg leading-relaxed">
                It is critical for compliance with regulations, enhancing customer experience, and ensuring the authenticity of an employee's residential address, especially when they have access to sensitive information.
              </p>
              
              <div className="mt-8 bg-red-900/30 p-6 rounded-lg border border-red-500/30">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-red-300 mr-2" />
                  Why It Matters
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Compliance with AML regulations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Enhanced customer experience</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Authenticity for employers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Prevents fraud and misrepresentation</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-full shadow-lg">
              <img 
                src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                alt="Address Verification" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent flex items-end">
                <p className="p-4 text-white font-medium">Ensuring address accuracy protects data integrity</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Verification Process Animation */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">The Verification Process</h2>
          
          <div className="relative">
            <div className="hidden md:block absolute top-0 left-1/2 w-1 bg-red-500/50 h-full transform -translate-x-1/2 z-0"></div>
            
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className={`relative z-10 transition-all duration-500 mb-8 ${
                  activeStep === index ? 'scale-100' : 'scale-95 opacity-70'
                }`}
              >
                <div className="flex flex-col md:flex-row items-center">
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:order-2 md:pl-12'}`}>
                    <div className={`bg-white/5 p-6 rounded-lg border border-white/10 transition-all duration-300 ${
                      activeStep === index ? 'bg-white/15 border-red-500/50 shadow-lg' : ''
                    }`}>
                      <div className="flex items-center mb-4 justify-center">
                        <div className={`p-3 rounded-full ${
                          activeStep === index ? 'bg-red-600/30' : 'bg-white/10'
                        }`}>
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-red-300 mb-2">{step.title}</h3>
                      <p className="text-white/80">{step.description}</p>
                    </div>
                  </div>
                  
                  <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center">
                    <div className={`w-10 h-10 rounded-full border-4 z-20 flex items-center justify-center transition-all duration-300 ${
                      activeStep === index 
                        ? 'bg-red-600 border-red-900 scale-125' 
                        : 'bg-red-900/70 border-red-700/50'
                    }`}>
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                  </div>
                  
                  <div className={`w-full md:w-1/2 mt-4 md:mt-0 ${index % 2 === 0 ? 'md:order-2 md:pl-12' : 'md:pr-12'}`}>
                    <div className={`h-48 rounded-lg overflow-hidden transition-all duration-300 ${
                      activeStep === index ? 'shadow-2xl ring-2 ring-red-500/50' : ''
                    }`}>
                      <img 
                        src={step.image} 
                        alt={step.title} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Digital vs Physical Verification */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Digital vs. Physical Verification</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Digital Verification */}
            <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Database className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Digital Verification</h3>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">AI-Based Platform</h4>
                      <p className="text-white/80">Assesses candidate's picture ID accuracy, classifying images as related or unrelated.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">GPS Enabled</h4>
                      <p className="text-white/80">Requires candidate presence at the designated location for completion.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Simple and Faster</h4>
                      <p className="text-white/80">Completes the process in minutes with minimal effort.</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 bg-red-900/30 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">Benefits:</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Speed & Efficiency</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Reduced Costs</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Increased Accuracy</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Enhanced Security</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/5673489/pexels-photo-5673489.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Digital map and GPS theme
                  alt="Digital Verification" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Digital systems streamline address verification</p>
                </div>
              </div>
            </div>
            
            {/* Physical Verification */}
            <div className="bg-gradient-to-br from-red-900/20 to-red-700/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <MapPin className="h-8 w-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Physical Verification</h3>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">On-Site Verification</h4>
                      <p className="text-white/80">A team physically visits to confirm the candidate's location.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">GPS Location Capture</h4>
                      <p className="text-white/80">Records GPS coordinates of the candidate's location.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Location Imagery</h4>
                      <p className="text-white/80">Photographs of the candidate's surroundings are documented.</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 bg-red-900/30 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">When Necessary:</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>High-Risk Cases</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Limited Digital Access</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Regulatory Requirements</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Suspicion of Fraud</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Team on-site verification
                  alt="Physical Verification" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Physical checks ensure reliability in critical scenarios</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Statistics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 initially-hidden opacity-0">
          {[
            {
              number: "90%",
              text: "of employers verify addresses",
              color: "from-red-600/20 to-red-400/20",
              border: "border-red-500/30"
            },
            {
              number: "15%",
              text: "of candidates misreport addresses",
              color: "from-pink-600/20 to-pink-400/20",
              border: "border-pink-500/30"
            },
            {
              number: "2-4",
              text: "days for physical verification",
              color: "from-orange-600/20 to-orange-400/20",
              border: "border-orange-500/30"
            },
            {
              number: "<1",
              text: "hour for digital verification",
              color: "from-yellow-600/20 to-yellow-400/20",
              border: "border-yellow-500/30"
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br ${stat.color} rounded-xl p-6 shadow-xl ${stat.border} text-center`}
            >
              <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
              <p className="text-white/80">{stat.text}</p>
            </div>
          ))}
        </div>
        
        {/* Call to Action */}
        <div className="bg-gradient-to-r from-red-600/30 to-pink-600/30 rounded-2xl p-8 shadow-2xl border border-red-500/20 text-center initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-4">Ready to Verify Addresses Securely?</h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Safeguard your organization from fraud and ensure data integrity with comprehensive address verification solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Get Started
            </button>
            <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
              Learn More
            </button>
          </div>
        </div>
      </section>

      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 1s ease forwards;
        }

        .animate-fadeInDown {
          animation: fadeInDown 1s ease forwards;
        }

        .animate-fadeInUp {
          animation: fadeInUp 1s ease forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AddressVerification;